/**
 * Same-host redirects for edu (§2.2). pathbuddy.com keeps today's page
 * for any path that already has one; HostGate decides that.
 * `/case-studies` with no slug is not in this table. It stays `/cases`.
 *
 * A one-segment `/insights/:slug` uses the host exam (LNAT on an edu apex).
 * A slug that is an article for that exam goes to `/insights/{exam}/{slug}`.
 * Any other slug goes to that exam's insight index.
 */
import articleSlugs from './content/insights/slugs.json';

export const CASE_STUDIES_INDEX_TARGET = '/cases';

export const EXAM_SLUGS = [
  'lnat',
  'tmua',
  'tara',
  'ucat',
  'esat',
  'oxbridge',
  'medicine',
  'degree-apprenticeships',
];

// Insights tabs: one per exam, plus guides that are not tied to one exam.
export const INSIGHT_SECTIONS = EXAM_SLUGS.concat(['general']);

export const SAME_HOST_REDIRECTS = [
  { path: '/lnat-prep', to: '/lnat' },
  { path: '/specialty/lnat', to: '/lnat' },
  { path: '/tmua-prep', to: '/tmua' },
  { path: '/specialty/tmua', to: '/tmua' },
  { path: '/ucat-prep', to: '/ucat' },
  { path: '/specialty/ucat', to: '/ucat' },
  { path: '/tara-prep', to: '/tara' },
  { path: '/specialty/tara', to: '/tara' },
  { path: '/esat-prep', to: '/esat' },
  { path: '/specialty/esat', to: '/esat' },
  { path: '/oxbridge-prep', to: '/oxbridge' },
  { path: '/specialty/oxbridge', to: '/oxbridge' },
  { path: '/medicalschool', to: '/medicine' },
  { path: '/medicalschool-prep', to: '/medicine' },
  { path: '/specialty/medicalschool', to: '/medicine' },
  { path: '/degreeapprenticeship', to: '/degree-apprenticeships' },
  { path: '/degreeapprenticeship-prep', to: '/degree-apprenticeships' },
  { path: '/specialty/degreeapprenticeship', to: '/degree-apprenticeships' },
  { path: '/degree-apprenticeships-prep', to: '/degree-apprenticeships' },
  { path: '/specialty/degree-apprenticeships', to: '/degree-apprenticeships' },
  { path: '/method/medicalschool', to: '/method/medicine' },
  { path: '/method/degreeapprenticeship', to: '/method/degree-apprenticeships' },
  { path: '/method/pathbuddy', to: '/method' },
  { path: '/features/chat', to: '/features' },
  { path: '/features/fluid', to: '/features' },
  { path: '/features/studio', to: '/features' },
  { path: '/features/apps', to: '/features' },
  { path: '/case-studies/handsworth-grammar', to: '/for-schools/handsworth' },
  { path: '/case-studies/clifton-college', to: '/for-schools/clifton' },
];

const FEATURE_PAGES = new Set([
  '/features',
  '/features/cases',
  '/features/interviews',
  '/features/paths',
  '/features/builder',
]);

function normalize(pathname) {
  const raw = (pathname || '/').split('?')[0].split('#')[0];
  if (!raw || raw === '/') return '/';
  const withSlash = raw.charAt(0) === '/' ? raw : `/${raw}`;
  if (withSlash.length > 1 && withSlash.charAt(withSlash.length - 1) === '/') {
    return withSlash.slice(0, -1);
  }
  return withSlash;
}

function insightExam(hostExam) {
  return EXAM_SLUGS.indexOf(hostExam) === -1 ? 'lnat' : hostExam;
}

export function resolveSameHostRedirect(pathname, hostExam = 'lnat') {
  const path = normalize(pathname);
  if (path === '/case-studies') return null;

  for (let i = 0; i < SAME_HOST_REDIRECTS.length; i += 1) {
    if (SAME_HOST_REDIRECTS[i].path === path) return SAME_HOST_REDIRECTS[i].to;
  }

  if (path.indexOf('/features/') === 0 && !FEATURE_PAGES.has(path)) return '/features';

  if (path.indexOf('/insights/') === 0) {
    const parts = path.split('/').filter(Boolean);
    if (parts.length === 2 && INSIGHT_SECTIONS.indexOf(parts[1]) === -1) {
      const exam = insightExam(hostExam);
      const known = articleSlugs[exam];
      if (Array.isArray(known) && known.indexOf(parts[1]) !== -1) {
        return `/insights/${exam}/${parts[1]}`;
      }
      return `/insights/${exam}`;
    }
  }

  if (path.indexOf('/case-studies/') === 0) {
    const slug = path.slice('/case-studies/'.length);
    if (slug && slug.indexOf('/') === -1) return '/for-schools';
  }

  return null;
}
